import { Component, ElementRef, Inject, ViewChild } from "@angular/core";
import icClose from "@iconify/icons-ic/twotone-close";
import { FormGroup, FormControl, Validators, NgForm } from "@angular/forms";
import { fadeInUp400ms } from "../../../..//../@vex/animations/fade-in-up.animation";
import { stagger60ms } from "../../../../../@vex/animations/stagger.animation";
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from "@angular/material/form-field";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { AuthService } from "src/app/pages/auth/authServices";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import * as html2pdf from "html2pdf.js";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { ConfigService } from "src/app/services/config.service";

@Component({
  selector: "vex-view-pdf",
  templateUrl: "./view-pdf.component.html",
  styleUrls: ["./view-pdf.component.scss"],
  animations: [fadeInUp400ms, stagger60ms],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: "standard",
      } as MatFormFieldDefaultOptions,
    },
  ],
})
export class ViewPDFComponent {
  FileURL: string;
  private authStatusSub: Subscription;
  isLoading = false;
  numRegex = /^-?\d*[.,]?\d{0,4}$/;
  isSubmitting = false;
  icClose = icClose;
  @ViewChild("pdfContent") pdfContent!: ElementRef;
  userIsAuthenticated = false;
  userId: string;
  fullName: string;
  sign: string;
  email: string;
  acceptanceDate: Date | null = null;
  isManualAcceptance = false;
  externalAcceptanceUrl = "";
  recordedByName = "";
  identityKind: "attorney" | "insurance" | "name" = "name";
  attorneyName = "";
  firmName = "";
  insuranceCompanyName = "";
  adjusterName = "";
  constructor(
    private authService: AuthService,
    public route: ActivatedRoute,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<ViewPDFComponent>,
    private configService: ConfigService,

    @Inject(MAT_DIALOG_DATA) public data: { mode: string; data: any; type: any }
  ) {
    if (data && data.type === "Applicant") {
      const firstName = data?.data?.applicantName || '';
      const middleName = data?.data?.applicantMiddleName || '';
      const lastName = data?.data?.applicantLastName || '';
      
      this.fullName = [firstName, middleName, lastName]
        .filter(name => !!name) // remove undefined or empty parts
        .join(' ');
      this.sign = data?.data?.applicantSign;
      this.email = data?.data?.email;
    } else if (data && data.type === "ApplicantAttorney") {
      this.fullName = data?.data?.f_name;
      this.sign = data?.data?.Attorneysign;
      this.email = data?.data?.AttorneyEmail;
    } else if (data && data.type === "Insurance") {
      this.fullName = data?.data?.insuranceCompany;
      this.sign = data?.data?.insurancesign;
      this.email = data?.data?.insuranceEmail;
    }
    else if (data && data.type === "DA") {
      console.log(data.data, "de")
      this.fullName = data?.data?.defenseFullName;
      this.sign = data?.data?.defenseSign;
      this.email = data?.data?.defenseEmail;
    }
    else if (data && data.type === "AA") {
      this.fullName = data?.data?.applicantAttorneyFullName;
      this.sign = data?.data?.applicantAttorneySign;
      this.email = data?.data?.applicantAttorneyEmail;
    } else if (data && data.type === "Employer") {
      this.fullName = data?.data?.employer;
      this.sign = data?.data?.employerSign;
      this.email = data?.data?.employerEmail;
    }

    this.setPartyIdentity(data);

    const currentParty = data?.data?.partyDigitalService?.[data?.type];
    if (currentParty?.currentEmail) {
      this.email = currentParty.currentEmail;
    }
    this.acceptanceDate = currentParty
      ? this.resolveAcceptanceDate({ updatedAt: currentParty.acceptedAt })
      : this.resolveAcceptanceDate(data?.data);
    this.applyManualAcceptance(data?.data, currentParty);
  }

  private setPartyIdentity(data: { type?: string; data?: any }): void {
    const record = data?.data || {};
    const type = data?.type;

    if (type === "ApplicantAttorney" || type === "AA" || type === "DA") {
      this.identityKind = "attorney";
      if (type === "DA") {
        this.attorneyName =
          this.text(record.defenseFullName) ||
          this.joinName(record.defenseName, record.defenseNamemiddle, record.defenseNamelast);
        this.firmName = this.text(record.defenseFirm);
      } else if (type === "AA") {
        this.attorneyName =
          this.text(record.applicantAttorneyFullName) ||
          this.joinName(
            record.applicantAttorneyname,
            record.applicantAttorneymiddle,
            record.applicantAttorneylast
          );
        this.firmName = this.text(record.firm);
      } else {
        this.attorneyName =
          this.text(record.f_name) ||
          this.joinName(record.Attorneyname, record.m_name, record.l_name);
        this.firmName = this.text(record.firm);
      }
      return;
    }

    if (type === "Insurance") {
      this.identityKind = "insurance";
      this.insuranceCompanyName = this.text(record.insuranceCompany);
      this.adjusterName =
        this.text(record.adjuster_fullName) ||
        this.joinName(record.adjuster, record.adjuster_middleName, record.adjuster_lastName);
    }
  }

  private text(value: unknown): string {
    return String(value ?? "").trim();
  }

  private joinName(...parts: unknown[]): string {
    return parts.map((part) => this.text(part)).filter(Boolean).join(" ");
  }

  private applyManualAcceptance(record: any, party: any): void {
    const method = party?.acceptanceMethod || record?.digitalServiceAcceptanceMethod || null;
    this.isManualAcceptance = method === "manual_external";
    if (!this.isManualAcceptance) {
      return;
    }
    this.sign = "";
    this.externalAcceptanceUrl = this.safeHttpUrl(
      party?.externalAcceptanceUrl || record?.digitalServiceExternalAcceptanceUrl
    );
    this.recordedByName = party?.acceptedByName || record?.digitalServiceAcceptedByName || "";
    const recordedAt = party?.recordedAt || record?.digitalServiceAcceptedAt;
    this.acceptanceDate = this.resolveAcceptanceDate({ updatedAt: recordedAt });
  }

  private safeHttpUrl(value: unknown): string {
    const url = String(value || "").trim();
    if (!url || url.length > 2048 || /\s/.test(url)) {
      return "";
    }
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "";
      }
      if (!parsed.hostname || parsed.username || parsed.password) {
        return "";
      }
      return url;
    } catch {
      return "";
    }
  }

  private resolveAcceptanceDate(record: any): Date | null {
    const raw = record?.updatedAt || record?.createdAt;
    if (!raw) {
      return null;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  ngOnInit(): void {
    // this.isLoading = true;
    this.FileURL = this.configService.get('fileURL');
    this.setupAuthentication();
  }

  private setupAuthentication() {
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe((isAuthenticated) => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
        // this.isLoading = false;
      });
  }

  downloadPDF() {
    const options = {
      filename: "Consent_to_Electronic_Service.pdf",
      image: { type: "jpeg", quality: 0.99 },
      html2canvas: { scale: 10, logging: true, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    const content: Element = this.pdfContent.nativeElement;

    html2pdf().from(content).set(options).save();
  }

  ngOnDestroy() {
    this.authStatusSub.unsubscribe();
  }
}
